// Shared "unsaved changes" UX so every editable page behaves the same:
//   - toggles a Save button between enabled/disabled,
//   - shows/hides a fixed "You have unsaved changes" toast,
//   - warns via beforeunload before the tab is closed or reloaded,
//   - guards in-page navigation (e.g. a week dropdown) that would otherwise
//     silently discard edits.
//
// Pure DOM; the fade runs via the Web Animations API (no jQuery).
// Selectors are resolved lazily on each call, so it survives Turbo body
// swaps. Each page decides WHAT counts as dirty and calls setDirty(true/false).
// Elke tracker meldt zich hier aan, zodat `navigateGuarded()` weet of er op
// deze pagina iets openstaat. Een module wordt maar één keer geëvalueerd, dus
// na een Turbo-wissel blijft een tracker van de vorige pagina bestaan: daarom
// vraagt `navigateGuarded` eerst of zijn ankerelement nog op het scherm staat.
// (De lijst legen bij `turbo:load` kán niet — dat event vuurt óók na de eerste
// load, dus dan gooide het de tracker weg die net was aangemeld.)
const trackers = new Set()

// Ga naar `url`, maar vraag eerst — in de eigen dialoog — wat er met
// niet-opgeslagen wijzigingen moet gebeuren. Gebruik dit overal waar een
// script zelf navigeert (unit-/afdelingskeuze, "terug naar de cockpit", een
// weekchip): een kale `window.location` laat de browser zijn eigen, niet te
// stylen beforeunload-venster tonen.
export function navigateGuarded (url) {
  const go = () => { window.location.href = url }
  for (const t of trackers) {
    if (t.onPage() && t.isDirty()) { t.guard(go); return }
  }
  go()
}

export function unsavedTracker (opts = {}) {
  const el = (sel) => (sel ? document.querySelector(sel) : null)
  let dirty = false
  let applied = null
  let released = false

  function toggle (node, on) {
    if (!node) return
    // Een lopende fade-out eerst afbreken (jQuery deed dat met stop(true, true)).
    // Anders verborg een fade die al liep de melding alsnog, als er binnen 150 ms
    // na het opslaan weer iets werd gewijzigd.
    if (node._aimFade) { node._aimFade.onfinish = null; node._aimFade.cancel(); node._aimFade = null }
    if (on) {
      node.style.display = ''
      // stylesheet kan display:none afdwingen — expliciet tonen, zoals
      // jQuery's fadeIn dat deed
      if (getComputedStyle(node).display === 'none') node.style.display = 'block'
      if (node.animate) node.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150 })
    } else if (node.animate) {
      const anim = node.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150 })
      node._aimFade = anim
      anim.onfinish = () => { node.style.display = 'none'; node._aimFade = null }
    } else {
      node.style.display = 'none'
    }
  }

  function setDirty (v) {
    dirty = !!v
    if (dirty) released = false // a fresh edit re-arms the guard after a release()
    if (applied === dirty) return // idempotent — no DOM churn when called repeatedly (e.g. during a drag)
    applied = dirty
    const s = el(opts.saveBtn); if (s) s.disabled = !dirty
    const d = el(opts.discardBtn); if (d) d.classList.toggle('invisible', !dirty)
    toggle(el(opts.banner), dirty)
  }

  // Ask what to do about unsaved changes — in the app's OWN styled dialog when
  // present (window.appConfirm), else the native confirm. Resolves a Promise of
  // 'save' | 'leave' | 'stay'. Only offers "Save" when an onSaveAll was given.
  // Labels in de taal van de pagina (data-i18n op de gedeelde confirm-modal),
  // met Engels als terugval.
  function label (key, fallback) {
    const el = document.getElementById('app-confirm-modal')
    if (!el) return fallback
    try { return (JSON.parse(el.dataset.i18n || '{}'))[key] || fallback } catch (e) { return fallback }
  }

  function confirmLeave () {
    const msg = opts.message || label('unsavedGeneric', 'You have unsaved changes on this page.')
    const canSave = typeof opts.onSaveAll === 'function'
    if (window.appConfirm) {
      if (canSave) {
        return window.appConfirm(msg, {
          title: label('unsavedTitle', 'Unsaved changes'), okText: label('saveAndLeave', 'Save & leave'),
          cancelText: label('stay', 'Stay'), extraText: label('leaveWithoutSaving', 'Leave without saving'),
          extraClass: 'btn-alt'
        }).then((r) => (r === true ? 'save' : r === 'extra' ? 'leave' : 'stay'))
      }
      return window.appConfirm(msg + ' ' + label('leaveQ', 'Leave without saving?'), {
        title: label('unsavedTitle', 'Unsaved changes'), danger: true,
        okText: label('leave', 'Leave'), cancelText: label('stay', 'Stay')
      }).then((ok) => (ok ? 'leave' : 'stay'))
    }
    return Promise.resolve(window.confirm(msg + ' ' + label('leaveQ', 'Leave without saving?')) ? 'leave' : 'stay')
  }

  // Resolve the prompt and, unless the user stays, run `done` (the navigation).
  // A failed Save keeps them on the page. Resolves true when navigation proceeds.
  function resolveLeave (done) {
    return confirmLeave().then((choice) => {
      if (choice === 'stay') return false
      if (choice === 'save') {
        return Promise.resolve(opts.onSaveAll()).then(
          () => { released = true; dirty = false; done(); return true },
          () => false // save failed → stay put
        )
      }
      released = true; dirty = false; done(); return true // leave without saving
    })
  }

  // Unavoidable native prompt for HARD unloads only (tab close, refresh, a form
  // submit that didn't release()). In-app link navigation is handled on-brand below.
  window.addEventListener('beforeunload', function (e) {
    if (dirty && !released) { e.preventDefault(); e.returnValue = '' }
  })

  // In-app navigation guard: intercept same-origin link clicks in the CAPTURE
  // phase (the sidebar uses data-turbo:false = a hard navigation, so we must
  // act before the browser leaves) and show the app's own dialog instead.
  document.addEventListener('click', function (e) {
    if (!dirty || released) return
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    const a = e.target.closest ? e.target.closest('a[href]') : null
    if (!a) return
    const href = a.getAttribute('href')
    if (!href || href.charAt(0) === '#' || /^(javascript|mailto|tel):/i.test(href)) return
    if (a.hasAttribute('download')) return
    if (a.target && a.target !== '_self') return
    if (a.origin !== window.location.origin) return // external → native beforeunload covers it
    if (a.pathname === window.location.pathname && a.search === window.location.search) return
    e.preventDefault()
    e.stopPropagation()
    const url = a.href
    resolveLeave(() => { window.location.href = url })
  }, true)

  // Anker om te zien of deze pagina nog gerenderd is (zie `trackers` boven).
  const anchor = opts.banner || opts.saveBtn || opts.discardBtn

  const api = {
    setDirty,
    isDirty: () => dirty,
    onPage: () => (anchor ? !!el(anchor) : true),
    markSaved: () => setDirty(false),
    // Suppress the guard for the NEXT navigation without touching the Save button —
    // for a real form submit, where disabling the submit button in the same click
    // would cancel the submit. Any later edit re-arms the guard.
    release () { released = true },
    // For JS-driven navigations (e.g. a week dropdown): the same styled prompt.
    // Runs `proceed` on leave/save, `cancel` on stay.
    guard (proceed, cancel) {
      if (!dirty) { proceed(); return }
      resolveLeave(() => proceed()).then((left) => { if (!left && cancel) cancel() })
    }
  }

  trackers.add(api)
  return api
}
